import { motion } from "framer-motion";
import { Phone, Mail, Building2, Factory, MessageCircle, MapPin, Globe } from "lucide-react";
import { buildWhatsAppLink } from "../data/config";

export const Contact = ({ content }) => {
  const contact = content.contact;
  const section = content.contactSection;
  const waLink = buildWhatsAppLink(
    "Hi evLUXE, I am interested in your electric scooters. Please contact me.",
    contact.whatsappNumber
  );

  const cards = [
    {
      icon: Phone,
      title: section.callTitle,
      testid: "contact-call",
      body: contact.phones.map((p) => (
        <a key={p.tel} href={`tel:${p.tel}`} className="block text-white/75 hover:text-brand transition-colors">
          {p.display}
        </a>
      )),
    },
    {
      icon: Mail,
      title: section.emailTitle,
      testid: "contact-email",
      body: (
        <a href={`mailto:${contact.email}`} className="text-white/75 hover:text-brand transition-colors break-all">
          {contact.email}
        </a>
      ),
    },
    {
      icon: MessageCircle,
      title: section.whatsappTitle,
      testid: "contact-whatsapp",
      body: (
        <a href={waLink} target="_blank" rel="noopener noreferrer" className="text-white/75 hover:text-brand transition-colors">
          {content.actions.chatWhatsApp}
        </a>
      ),
    },
    {
      icon: Globe,
      title: section.websiteTitle,
      testid: "contact-website",
      body: (
        <a href={contact.websiteUrl} target="_blank" rel="noopener noreferrer" className="text-white/75 hover:text-brand transition-colors">
          {contact.website}
        </a>
      ),
    },
    {
      icon: Building2,
      title: contact.corporateOffice.label,
      testid: "contact-corporate",
      body: (
        <div className="space-y-2">
          <p className="text-white/70 text-sm leading-relaxed">{contact.corporateOffice.address}</p>
          <a href={contact.corporateOffice.maps} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-brand text-sm hover:underline">
            <MapPin size={14} /> {content.actions.viewMap}
          </a>
        </div>
      ),
    },
    {
      icon: Factory,
      title: contact.manufacturingUnit.label,
      testid: "contact-manufacturing",
      body: (
        <div className="space-y-2">
          <p className="text-white/70 text-sm leading-relaxed">{contact.manufacturingUnit.address}</p>
          <a href={contact.manufacturingUnit.maps} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-brand text-sm hover:underline">
            <MapPin size={14} /> {content.actions.viewMap}
          </a>
        </div>
      ),
    },
  ];

  return (
    <section id="contact" data-testid="contact-section" className="relative py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-2xl mx-auto mb-14"
        >
          <span className="section-kicker justify-center mb-4">{section.kicker}</span>
          <h2 className="font-display font-bold text-4xl sm:text-5xl">{section.title}</h2>
          <p className="mt-5 text-white/60">
            {section.description}
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {cards.map((c, i) => {
            const Icon = c.icon;
            return (
              <motion.div
                key={c.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.5, delay: (i % 3) * 0.08 }}
                data-testid={c.testid}
                className="glass rounded-lg p-7 hover:-translate-y-1 hover:border-brand/50 transition-all duration-300"
              >
                <div className="w-12 h-12 rounded-lg bg-brand/10 flex items-center justify-center mb-4">
                  <Icon className="text-brand" size={22} />
                </div>
                <h3 className="font-display font-semibold text-lg mb-2">{c.title}</h3>
                <div>{c.body}</div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Contact;
